'use client';

import { useDisclosure } from '@mantine/hooks';
import { createContext, useCallback, useContext, useState } from 'react';

interface HeaderContextType {
  showSloganInput: boolean;
  setShowSloganInput: (value: boolean) => void;
  secondaryTitle: string;
  setSecondaryTitle: (value: string) => void;
  isSloganEditable: boolean;
  setIsSloganEditable: (value: boolean) => void;
  isPreviewSlogan: boolean;
  setIsPreviewSlogan: (value: boolean) => void;
  infoModalOpened: boolean;
  openInfoModal: () => void;
  closeInfoModal: () => void;
  refreshSlogan: boolean;
  setRefreshSlogan: (value: boolean) => void;
}

const HeaderContext = createContext<HeaderContextType | undefined>(undefined);

const HeaderProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [secondaryTitle, setSecondaryTitle] = useState('');
  const [isSloganEditable, setIsSloganEditable] = useState(false);
  const [isPreviewSlogan, setIsPreviewSlogan] = useState(false);
  const [showSloganInput, setShowSloganInput] = useState(false);
  const [refreshSlogan, setRefreshSlogan] = useState(false);

  const [infoModalOpened, { open: openInfoModal, close: closeInfoModal }] =
    useDisclosure(false);

  const handleSetSecondaryTitle = useCallback((value: string) => {
    setSecondaryTitle(value);
  }, []);

  const handleSetIsSloganEditable = useCallback((value: boolean) => {
    setIsSloganEditable(value);
  }, []);

  const handleSetIsPreviewSlogan = useCallback((value: boolean) => {
    setIsPreviewSlogan(value);
  }, []);

  const value = {
    showSloganInput,
    setShowSloganInput,
    secondaryTitle,
    setSecondaryTitle: handleSetSecondaryTitle,
    isSloganEditable,
    setIsSloganEditable: handleSetIsSloganEditable,
    isPreviewSlogan,
    setIsPreviewSlogan: handleSetIsPreviewSlogan,
    infoModalOpened,
    openInfoModal,
    closeInfoModal,
    refreshSlogan,
    setRefreshSlogan,
  };

  return (
    <HeaderContext.Provider value={value}>{children}</HeaderContext.Provider>
  );
};

const useHeader = () => {
  const context = useContext(HeaderContext);
  if (!context) {
    throw new Error('useHeader must be used within HeaderProvider');
  }
  return context;
};

export { HeaderProvider, useHeader };
